import React, { useState, useRef, useEffect } from 'react';
import { Eye, Hash, ArrowRight, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';

const CODE_LENGTH = 6;

export default function Landing({ onResolved }) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function onChange(e) {
    const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH);
    setCode(v);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (code.length !== CODE_LENGTH) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.resolveCode(code);
      onResolved(res.sessionId, res.token);
    } catch (err) {
      setError(err.message || 'That code didn\'t work. Please double-check with our technician.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#f7953c] rounded-2xl mb-4">
              <Eye className="w-9 h-9 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#f7953c]">Clarity IT Remote Help</h1>
            <p className="text-slate-400 mt-2 text-sm">
              Enter the 6-character code your technician shared with you, or open the link they sent.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 space-y-5"
          >
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Invite code</label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="text"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={onChange}
                  placeholder="ABC123"
                  className="w-full pl-10 pr-3 py-4 bg-slate-950 border border-slate-700 rounded-xl text-2xl font-mono tracking-[0.4em] uppercase text-center text-[#f7953c] focus:outline-none focus:ring-2 focus:ring-[#f7953c] focus:border-transparent transition-all placeholder-slate-700"
                />
              </div>
              <div className="text-xs text-slate-500 mt-2">
                {code.length}/{CODE_LENGTH} characters
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-300">{error}</div>
              </div>
            )}

            <button
              type="submit"
              disabled={code.length !== CODE_LENGTH || submitting}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#f7953c] hover:bg-[#e88729] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Connecting…
                </>
              ) : (
                <>
                  Continue <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>

          <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-4 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-slate-400 leading-relaxed">
              On the next screen we'll ask for camera and microphone access so our technician can help.
              Your camera turns off the moment the session ends.
            </div>
          </div>
        </div>
      </div>

      <div className="text-center text-xs text-slate-600 py-4">
        Clarity IT · 07 55 000 457
      </div>
    </div>
  );
}
